import { Input, Output, EventEmitter, OnChanges, SimpleChanges, Directive } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { BaseMetricComponent } from './base-metric.component';
import { TeamMetricResult, CalculationLogic, DEFAULT_CALCULATION_LOGIC } from 'src/app/models/team-metrics';
import { TeamMetricsService } from 'src/app/services/team-metrics.service';

@Directive()
export abstract class BaseMetricWithCalculationLogicComponent<T extends TeamMetricResult> extends BaseMetricComponent<T> implements OnChanges {
  @Input() calculationLogic: CalculationLogic | null = DEFAULT_CALCULATION_LOGIC;
  @Output() loadingStateChange = new EventEmitter<boolean>();

  private previousCalculationLogic: CalculationLogic = DEFAULT_CALCULATION_LOGIC;

  constructor(teamMetricsService: TeamMetricsService) {
    super(teamMetricsService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const calcLogicChange = changes['calculationLogic'];
    const mainFiltersChanged = this.haveMainFiltersChanged(changes);

    if (calcLogicChange && !mainFiltersChanged) {
      const currentCalcLogic = this.calculationLogic ?? DEFAULT_CALCULATION_LOGIC;
      if (currentCalcLogic !== this.previousCalculationLogic) {
        this.previousCalculationLogic = currentCalcLogic;
        if (this.canRecalculate()) {
          this.recalculateMetric(currentCalcLogic);
        } else {
          super.ngOnChanges(changes);
        }
      }
      return;
    }
    
    this.previousCalculationLogic = this.calculationLogic ?? DEFAULT_CALCULATION_LOGIC;
    super.ngOnChanges(changes);
  }

  haveMainFiltersChanged(changes: SimpleChanges): boolean {
    const teamChanged = changes['team'];
    const startDateChanged = changes['startDate'];
    const endDateChanged = changes['endDate'];
    
    const currentTeam = this.team ?? '';
    const currentStartDate = this.startDate ? this.startDate.toISOString() : '';
    const currentEndDate = this.endDate ? this.endDate.toISOString() : '';

    return (teamChanged && currentTeam !== this.previousInputs.team) ||
           (startDateChanged && currentStartDate !== this.previousInputs.startDate) ||
           (endDateChanged && currentEndDate !== this.previousInputs.endDate);
  }

  protected abstract canRecalculate(): boolean;
  protected abstract recalculateMetric(newLogic: CalculationLogic): void;

  protected emitLoadingState(isLoading: boolean): void {
    this.loadingStateChange.emit(isLoading);
  }

  protected callFetchMetric(
    team: string, 
    isoStartDate: string, 
    isoEndDate: string,
    org: string,
    project: string
  ): Observable<T | null> {
    this.emitLoadingState(true);
    
    return this.callFetchMetricWithCalculationLogic(
      team, 
      isoStartDate, 
      isoEndDate, 
      this.calculationLogic ?? DEFAULT_CALCULATION_LOGIC,
      org,
      project
    ).pipe(
      tap(() => this.emitLoadingState(false)),
      catchError(err => {
        this.emitLoadingState(false);
        return throwError(() => err);
      })
    );
  }

  protected abstract callFetchMetricWithCalculationLogic(
    team: string, 
    isoStartDate: string, 
    isoEndDate: string,
    calculationLogic: CalculationLogic,
    org: string,
    project: string
  ): Observable<T | null>;
}
